// Copy and layout constants for the promo-expiry card and its Review Your Promotions modal.
//
// These literals have moved more than once across design revisions, so they live here and are asserted
// verbatim in tests rather than typed inline.

// ---- Card ----------------------------------------------------------------------------------------

export const CARD_CHIP_LABEL = "Performance Review";
export const CARD_HEADLINE = "Time to Review Your Promotions";
export const CARD_BODY =
  "Some promotions have expired or are ending soon. Review their performance and extend or replace them to maintain demand.";

// ⚠ Reads "Dismiss" but behaves like Maybe Later, and that is deliberate — feedback asked for the
// wording and placement to match BnberryCard's pending state, not for the behaviour to change.
//
// The dismissal is persisted, but scoped PER TIER: the card genuinely returns at the next threshold.
// BnberryCard uses "Dismiss" for a permanent, server-side hide and keeps "Maybe Later" for its
// session-only one, so the same word now means two different lifetimes across the Action Centre. If
// that causes confusion, the fix is a copy decision, not a code one.
export const DISMISS_LABEL = "Dismiss";
export const REVIEW_AND_EXTEND_LABEL = "Review & Extend";

// The payload carries up to max-items promos; the card renders this many and hides the rest behind the
// expander. Keeping the payload larger than the visible count is what lets "+ N more" expand without a
// second request.
export const CARD_VISIBLE_ROWS = 3;

// ---- Modal ---------------------------------------------------------------------------------------

export const MODAL_TITLE = "Review Your Promotions";

// ⚠ Differs from the mockup image, which reads "Extend dates, update discounts, or deactivate offers as
// needed". "Update discounts" was dropped because the Adjustment column is read-only — promising an edit
// the modal does not offer is the same trap that phrase already caused once.
export const MODAL_SUBTITLE =
  "Expired and soon-to-expire promotions are shown below. Extend dates or deactivate offers as needed.";

export const MODAL_CLOSE_LABEL = "Close";
export const MODAL_SAVE_LABEL = "Save";
export const MODAL_SAVED_LABEL = "Saved";

// Shown beside the status toggle once a promo has been deactivated. The toggle's label otherwise
// carries the days-left text, which contradicts an off switch — "3 days left" next to a retired promo
// reads as a rendering bug.
export const MODAL_INACTIVE_LABEL = "Inactive";
export const MODAL_NA_LABEL = "N/A";

/**
 * Shown instead of Save for a Hotel Trader pilot (GC-created) promo.
 *
 * The backend suppresses alerts for these, so such a row should never reach this modal. This is the
 * client-side backstop: their `discountVO` is an array, which the update payload builder reads as an
 * object, and since the endpoint replaces the configuration a save would rewrite a live promo as a flat
 * 0% discount. Too destructive to leave resting on a backend invariant alone.
 */
export const MODAL_MANAGED_LABEL = "Managed by Hotel Trader";

export const MODAL_COLUMNS = [
  "Promo Name",
  "Status",
  "Adjustment",
  "Booking Window",
  "Stay Dates",
  "Revenue",
];

// ---- Status pill ---------------------------------------------------------------------------------

export const PILL_EXPIRED = "Expired";
// daysLeft === 0 means the promo stops applying at end of today. "0 days left" reads as a bug.
export const PILL_EXPIRES_TODAY = "Expires today";

// ---- Adjustment ----------------------------------------------------------------------------------

// Shown when a promo's adjustment varies by day rather than being a single percentage.
export const ADJUSTMENT_DAILY_LABEL = "Daily";
export const ADJUSTMENT_DAILY_TOOLTIP =
  "This promotion uses a different adjustment for each day of the week.";
